import { useCallback, useEffect, useRef, useState } from "react";
import i18next from "i18next";
import { showToast } from "../sharedComponents/ToasterMessage/ToasterMessage";
import { getReactionConfig } from "./reactionSocket";
import { getBlockedEmojis } from "./useAllowedEmojis";
import {
  getMyReactionEmoji,
  isEmojiBlocked,
  parseSocketErrorMessage,
  toggleReactionLocally,
} from "./chatReactionUtils";

const EMOJI_NOT_ALLOWED_ERROR = "This emoji is not allowed as a reaction.";

const contextMatches = (payload = {}, context = {}) => {
  const checks = [
    ["tournamentId", context.tournamentId],
    ["eventId", context.eventId],
    ["teamId", context.teamId],
    ["conversationId", context.conversationId],
  ];
  return checks.every(([key, expected]) => {
    if (!expected || payload[key] == null) return true;
    return String(payload[key]) === String(expected);
  });
};

const useChatReactions = ({
  socket,
  chatType,
  context = {},
  currentUser,
  onUpdate,
} = {}) => {
  const [pickerMessageId, setPickerMessageId] = useState(null);
  const pendingRef = useRef(null);
  const onUpdateRef = useRef(onUpdate);
  const contextRef = useRef(context);
  onUpdateRef.current = onUpdate;
  contextRef.current = context;

  const closePicker = useCallback(() => setPickerMessageId(null), []);

  const togglePicker = useCallback((messageId) => {
    setPickerMessageId((prev) => (prev === messageId ? null : messageId));
  }, []);

  const handleReact = useCallback(
    (message, emoji) => {
      if (!socket || !message?._id || !emoji) return;

      const config = getReactionConfig(chatType);
      if (!config) return;

      const mine = getMyReactionEmoji(message.reactions, currentUser?._id);
      // Any emoji is allowed except the blocked list; removing my current
      // reaction is always allowed.
      if (mine !== emoji && isEmojiBlocked(emoji, getBlockedEmojis())) return;

      pendingRef.current = {
        messageId: message._id,
        previous: message.reactions || [],
      };

      onUpdateRef.current?.(
        message._id,
        toggleReactionLocally(message.reactions, currentUser, emoji)
      );

      socket.emit(
        config.emit,
        config.buildPayload({
          ...contextRef.current,
          messageId: message._id,
          emoji,
        })
      );
      closePicker();
    },
    [chatType, closePicker, currentUser, socket]
  );

  useEffect(() => {
    if (!socket) return;
    const config = getReactionConfig(chatType);
    if (!config) return;

    const handleUpdate = (payload = {}) => {
      const messageId = payload.messageId || payload.message?._id;
      const reactions = payload.reactions || payload.message?.reactions;
      if (!messageId || !Array.isArray(reactions)) return;
      if (!contextMatches(payload, contextRef.current)) return;

      if (
        pendingRef.current &&
        String(pendingRef.current.messageId) === String(messageId)
      ) {
        pendingRef.current = null;
      }
      onUpdateRef.current?.(messageId, reactions);
    };

    const handleError = (err) => {
      if (!pendingRef.current) return;
      const { messageId, previous } = pendingRef.current;
      pendingRef.current = null;
      onUpdateRef.current?.(messageId, previous);
      const message = parseSocketErrorMessage(err);
      if (message === EMOJI_NOT_ALLOWED_ERROR) {
        showToast(
          i18next.t("chatMessages.emojiNotAllowed", {
            ns: "common",
            defaultValue: message,
          }),
          "error"
        );
      } else if (message) {
        showToast(message, "error");
      }
    };

    socket.on(config.listen, handleUpdate);
    socket.on("error", handleError);

    return () => {
      socket.off(config.listen, handleUpdate);
      socket.off("error", handleError);
    };
  }, [socket, chatType]);

  useEffect(() => {
    if (!pickerMessageId) return undefined;

    const handlePointer = (event) => {
      if (event.target?.closest?.(".chat-reaction-picker, .chat-reaction-btn, .chat-bubble-actions.is-open")) {
        return;
      }
      closePicker();
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
    };
  }, [closePicker, pickerMessageId]);

  return {
    pickerMessageId,
    togglePicker,
    closePicker,
    handleReact,
  };
};

export default useChatReactions;
